import { currentTimestamp } from '@ardoq/date-time';
import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import {
  aggregatesWasTriggered,
  allFiltersCleared,
  drilldownWasTriggered,
  excelExportFailed,
  filterValueChanged,
  FilterValueChangedPayload,
  filterValueRemoved,
  forceReportRefresh,
  pageAndPaginationChanged,
  snapshotSelected,
  sortingChanged,
} from './actions';
import {
  ArdoqError,
  isArdoqError,
  isTruthy,
  setStateProperty,
  spreadPayloadOverState,
  toArdoqError,
} from '@ardoq/common-helpers';
import {
  keepValidFilters,
  NUMBER_OF_ROWS_PER_PAGE,
  ReportSort,
} from '@ardoq/report-reader';
import { combineLatest, map, Observable } from 'rxjs';
import {
  reportNavigation$,
  ReportNavigationState,
} from '../navigation/reportNavigation$';
import { ReportModule } from '../types';
import { reports$ } from 'streams/reports/reports$';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import {
  APIReportAttributes,
  APIReportFilters,
  ArdoqId,
  EnhancedPresentation,
  GraphlakeSnapshotId,
  isFilterExpressionArray,
  ReportColumn,
  ReportSlide,
  SelectedAggregate,
  SortOrder,
  WidgetDataSourceTypes,
} from '@ardoq/api-types';
import { isEmpty, isEqual, isNull, omit } from 'lodash';
import { reportBuilderOperations } from '@ardoq/report-builder';
import { viewPane$ } from 'presentation/viewPane/viewPane$';
import { slideInterface } from 'modelInterface/presentations/slideInterface';
import { loadedPresentation$ } from 'presentation/streams/loadedPresentation$';
import { fetchPresentationSearchResults } from './utils';
import { logError } from '@ardoq/logging';
import { ReportReader$State } from './types';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { dashboardBuilder$ } from '../../dashboard/DashboardBuilder/streams/DashboardBuilder$';
import { widgetOperations } from '@ardoq/dashboard';
import { FeatureContext, featureOperations, Features } from '@ardoq/features';
import { widgetsAndSettings$ } from '../../dashboard/DashboardBuilder/streams/widgetsAndSettings$';
import { featureContext$ } from '../../streams/featureContext$';
import { api, reportApi } from '@ardoq/api';
import {
  isReportCaptureMode$,
  toCapturePagination,
  toSlidePagination,
} from './capturePagination';

type ReportReaderInput$State = {
  report: APIReportAttributes | null;
  initialFilters: APIReportFilters | null;
  reportAPIError: ArdoqError | null;
};

const reportReaderInput$: Observable<ReportReaderInput$State> =
  reportNavigation$.pipe(
    filter(
      (
        nav
      ): nav is ReportNavigationState & {
        reportId: ArdoqId;
        stateId: ArdoqId | undefined;
      } => nav.reportModule === ReportModule.READER && isTruthy(nav.reportId)
    ),
    // TODO: add this back once in reportReader$ is not used through withLatestFrom in ai assistant routines,
    // which means reportReader$ is properly sub/unsub when ReportReader component is mounted/unmounted
    // distinctUntilChanged(
    //   (prev, curr) =>
    //     prev.reportId === curr.reportId &&
    //     prev.stateId === curr.stateId &&
    //     isEqual(prev.filters, curr.filters)
    // ),
    switchMap(async ({ reportId, stateId, filters }) => {
      const reportPromise = reportApi.fetch(reportId);
      const filtersStatePromise = stateId
        ? reportApi.fetchViewState(reportId, stateId)
        : Promise.resolve(null);

      const [report, filtersState] = await Promise.all([
        reportPromise,
        filtersStatePromise,
      ]);

      if (isArdoqError(report)) {
        if (!api.isNotFound(report)) {
          logError(report, 'Failed to fetch report for report reader');
        }
        return {
          report: null,
          reportAPIError: report,
          initialFilters: null,
        };
      }

      const hasFiltersStateError = isArdoqError(filtersState);
      const storedFilters = !hasFiltersStateError
        ? filtersState?.state.filters
        : null;
      const initialFilters = storedFilters ?? filters ?? null;
      return { report, initialFilters, reportAPIError: null };
    })
  );

const updatedReport$ = combineLatest([reportNavigation$, reports$]).pipe(
  map(([{ reportId }, reports]) => {
    const report =
      reportId && reports.byId[reportId] ? reports.byId[reportId] : null;
    return report;
  }),
  filter(isTruthy),
  distinctUntilChanged<APIReportAttributes>(isEqual)
);

const widgetFilterInput$ = combineLatest([
  dashboardBuilder$,
  widgetsAndSettings$,
  reports$,
]).pipe(
  filter(([{ isFilterDrawerOpened }]) => isFilterDrawerOpened),
  map(([, { widgets, selectedWidgetId }, reports]) => {
    const selectedWidget = widgets.find(
      widget => widget._id === selectedWidgetId
    );

    if (widgetOperations.isStaticContentWidgetData(selectedWidget))
      return {
        report: null,
        initialFilters: null,
        reportAPIError: null,
      };

    if (
      selectedWidget &&
      selectedWidget.datasource?.sourceType === WidgetDataSourceTypes.REPORT
    ) {
      const reportId = selectedWidget.datasource?.sourceId;
      const report =
        reportId && reports.byId[reportId] ? reports.byId[reportId] : null;

      if (!report) {
        return {
          report: null,
          initialFilters: null,
          reportAPIError: toArdoqError({ error: Error('Report not found') }),
        };
      }

      return {
        report,
        initialFilters: selectedWidget.filters ?? null,
        reportAPIError: null,
      };
    }
    return { report: null, initialFilters: null, reportAPIError: null };
  }),
  // has report metadata result
  filter(({ report, reportAPIError }) => Boolean(report || reportAPIError))
);

const initReportReader$State = (
  _state: ReportReader$State,
  {
    report,
    initialFilters,
    reportAPIError,
  }: {
    report: APIReportAttributes | null;
    initialFilters: APIReportFilters | null;
    reportAPIError: ArdoqError | null;
  }
): ReportReader$State => {
  if (!report) {
    return {
      ...defaultState,
      reportAPIError,
      hasFinishedFetchReport: true,
    };
  }

  return {
    ...defaultState,
    hasFinishedFetchReport: true,
    reportId: report._id,
    report,
    sort: reportBuilderOperations.getSortKeyAndOrder(report),
    filters: initialFilters,
    columns: report.columns,
    selectedAggregate: report.selectedAggregate,
    lastUpdated: report.lastUpdated,
    reportAPIError,
  };
};

const defaultState: ReportReader$State = {
  hasExcelExportFailed: false,
  isSearchBeingExecuted: false,
  isAggregatesBeingExecuted: false,
  page: 1,
  pagination: { from: 0, size: NUMBER_OF_ROWS_PER_PAGE },
  columns: [],
  sort: { columnKey: null, order: SortOrder.ASC },
  reportId: null,
  report: null,
  presentationId: null,
  slideId: null,
  hasFinishedFetchReport: false,
  filters: null,
  isFilteringDisabled: false,
  viewStateId: null,
  selectedAggregate: null,
  lastUpdated: '',
  reportAPIError: null,
  selectedSnapshot: null,
  paginationBeforeCapture: null,
};

type PresentationSlideInput = {
  slide: ReportSlide;
  presentation: EnhancedPresentation;
  currentUserId: ArdoqId;
  featureContext: FeatureContext;
};

const presentationSlideInput$ = combineLatest([
  viewPane$,
  loadedPresentation$,
  currentUser$,
  featureContext$,
]).pipe(
  map(
    ([viewPane, { presentation }, { _id: currentUserId }, featureContext]) => ({
      slide: viewPane.loadedSlide,
      presentation,
      currentUserId,
      featureContext,
    })
  ),
  filter(
    (
      presentationInputState
    ): presentationInputState is PresentationSlideInput => {
      const { slide, presentation } = presentationInputState;
      return Boolean(
        slide && presentation && slideInterface.isReportSlide(slide._id)
      );
    }
  ),
  distinctUntilChanged<PresentationSlideInput>(isEqual),
  switchMap(async presentationInputState => {
    const reportSlide = presentationInputState.slide;
    const sort = getSortFromSlide(reportSlide);
    const slideFilters = getFiltersFromSlide(reportSlide);

    const isPublicPresentation =
      presentationInputState.currentUserId === undefined;

    const hasFilteringFeature = featureOperations.hasFeature(
      presentationInputState.featureContext,
      Features.REPORT_FILTERING_IN_PRESENTATIONS
    );

    const slideAlreadyHasFilters = !isEmpty(slideFilters);

    // initial fetch to get the report meta data (columns) and check report existence
    // compare with reportReaderInput$, this acts as reportApi.fetch(reportId) before getting the search results
    const initialSearchResults = await fetchPresentationSearchResults({
      reportId: presentationInputState.slide.reportId,
      presentationId: presentationInputState.presentation._id,
      pagination: defaultState.pagination,
      slideId: presentationInputState.slide._id,
      sort,
      filters: slideFilters,
    });

    if (isArdoqError(initialSearchResults)) {
      return {
        reportAPIError: initialSearchResults,
        datasource: null,
        name: null,
        columns: [],
        description: null,
        filters: null,
        reportId: presentationInputState.slide.reportId,
        presentationId: presentationInputState.presentation._id,
        slideId: presentationInputState.slide._id,
        sort,
        selectedAggregate: null,
        isFilteringDisabled: true,
      };
    }

    // Ignore filter feature check if the slide is public see #20932
    const isFilteringDisabled =
      slideAlreadyHasFilters || (!hasFilteringFeature && !isPublicPresentation);
    return {
      ...initialSearchResults,
      reportAPIError: null,
      filters: initialSearchResults.filters ?? null,
      reportId: presentationInputState.slide.reportId,
      presentationId: presentationInputState.presentation._id,
      slideId: presentationInputState.slide._id,
      sort: sort
        ? sort
        : reportBuilderOperations.getSortKeyAndOrder(initialSearchResults),
      isFilteringDisabled,
    };
  })
);

const getSortFromSlide = (reportSlide: ReportSlide): ReportSort =>
  !isEmpty(reportSlide.reportParams)
    ? {
        columnKey: reportSlide.reportParams.sort ?? null,
        order: reportSlide.reportParams.order ?? SortOrder.ASC,
      }
    : defaultState.sort;

const getFiltersFromSlide = (
  reportSlide: ReportSlide
): APIReportFilters | null =>
  !isEmpty(reportSlide.reportParams?.filters)
    ? reportSlide.reportParams?.filters
    : null;

type PresentationSearchResultWithReportData = {
  reportId: ArdoqId;
  presentationId: ArdoqId;
  slideId: ArdoqId;
  sort: ReportSort;
  isFilteringDisabled: boolean;
  filters: APIReportFilters | null;
  reportAPIError: ArdoqError | null;
  columns: ReportColumn[];
  name: string | null;
  selectedAggregate: SelectedAggregate | null;
};
const initReportReader$StateWithPresentation = (
  state: ReportReader$State,
  presentationData: PresentationSearchResultWithReportData
): ReportReader$State => {
  return {
    ...state,
    ...toSlidePagination(state),
    reportId: presentationData.reportId,
    presentationId: presentationData.presentationId,
    slideId: presentationData.slideId,
    columns: presentationData.columns,
    sort: presentationData.sort,
    filters: presentationData.filters,
    hasFinishedFetchReport: true,
    isFilteringDisabled: presentationData.isFilteringDisabled,
    selectedAggregate: presentationData.selectedAggregate,
    reportAPIError: presentationData.reportAPIError,
  };
};

const updateFilterValue = (
  state: ReportReader$State,
  { columnKey, expression }: FilterValueChangedPayload
): ReportReader$State => {
  const newFilters =
    isNull(expression) || isEmpty(expression)
      ? {
          ...state.filters,
          [columnKey]: { values: [] },
        }
      : {
          ...state.filters,
          [columnKey]: {
            values: isFilterExpressionArray(expression)
              ? expression
              : [expression],
          },
        };

  const shouldResetPage = !isEqual(
    keepValidFilters(state.filters ?? {}, state.columns),
    keepValidFilters(newFilters, state.columns)
  );

  return {
    ...state,
    ...(shouldResetPage
      ? {
          page: 1,
          pagination: {
            from: 0,
            size: state.pagination.size,
          },
        }
      : {}),
    filters: newFilters,
  };
};

const removeFilterValue = (
  state: ReportReader$State,
  columnKey: ArdoqId
): ReportReader$State => {
  const newFilters = omit(state.filters, columnKey);

  const shouldResetPage = !isEqual(
    keepValidFilters(state.filters ?? {}, state.columns),
    keepValidFilters(newFilters, state.columns)
  );

  return {
    ...state,
    ...(shouldResetPage
      ? {
          page: 1,
          pagination: {
            from: 0,
            size: state.pagination.size,
          },
        }
      : {}),
    filters: newFilters,
  };
};

const selectSnapshot = (
  state: ReportReader$State,
  selectedSnapshot: GraphlakeSnapshotId | null
): ReportReader$State => ({
  ...state,
  page: 1,
  pagination: { from: 0, size: state.pagination.size },
  selectedSnapshot,
});

const removeAllFilters = (state: ReportReader$State): ReportReader$State => ({
  ...state,
  page: 1,
  pagination: {
    from: 0,
    size: state.pagination.size,
  },
  filters: null,
});

const handleReportUpdate = (
  state: ReportReader$State,
  updatedReport: APIReportAttributes
): ReportReader$State => ({
  ...state,
  columns: updatedReport.columns,
  selectedAggregate: updatedReport.selectedAggregate,
  sort: reportBuilderOperations.getSortKeyAndOrder(updatedReport),
  lastUpdated: updatedReport.lastUpdated,
});

const updateRefreshTimestamp = (
  state: ReportReader$State
): ReportReader$State => ({
  ...state,
  refreshTimestamp: currentTimestamp(),
});

export const reportReader$ = reducedStream<ReportReader$State>(
  'reportReader$',
  defaultState,
  [
    streamReducer(reportReaderInput$, initReportReader$State), // This initialize report metadata before request search results when opening a report in the core app
    streamReducer(widgetFilterInput$, initReportReader$State), // This initialize report metadata before request search results when opening a drawer in dashboard filtering
    streamReducer(
      presentationSlideInput$,
      initReportReader$StateWithPresentation
    ), // This initialize report metadata before request search results when opening a presentation
    streamReducer(updatedReport$, handleReportUpdate),
    streamReducer(isReportCaptureMode$, toCapturePagination),
    reducer(excelExportFailed, setStateProperty('hasExcelExportFailed')),
    reducer(allFiltersCleared, removeAllFilters),
    reducer(drilldownWasTriggered, setStateProperty('isSearchBeingExecuted')),
    reducer(
      aggregatesWasTriggered,
      setStateProperty('isAggregatesBeingExecuted')
    ),
    reducer(sortingChanged, setStateProperty('sort')),
    reducer(forceReportRefresh, updateRefreshTimestamp),
    reducer(filterValueChanged, updateFilterValue),
    reducer(filterValueRemoved, removeFilterValue),
    reducer(snapshotSelected, selectSnapshot),
    reducer(pageAndPaginationChanged, spreadPayloadOverState),
  ]
);
